import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { AutomotiveAftermarketFiltersService } from './automotive-aftermarket-filters.service';
import {
  AutomotiveAftermarketFiltersInitialState,
  AutomotiveAftermarketFiltersLoad,
  AutomotiveAftermarketFiltersUpdate,
} from './automotive-aftermarket-filters.actions';
import { MonitoringFilterNameDictionary } from '../../../../models';
import { CustomHttpErrorResponse, Error } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AutomotiveAftermarketFiltersStateModel {
  items: MonitoringFilterNameDictionary[];
  loaded: boolean;
}

const initialState: AutomotiveAftermarketFiltersStateModel = {
  items: [],
  loaded: false,
};

@State<AutomotiveAftermarketFiltersStateModel>({
  name: 'automotiveAftermarketFilters',
  defaults: initialState,
})
@Injectable()
export class AutomotiveAftermarketFiltersState {
  constructor(
    private automotiveAftermarketFiltersService: AutomotiveAftermarketFiltersService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: AutomotiveAftermarketFiltersStateModel): AutomotiveAftermarketFiltersStateModel {
    return state;
  }

  @Selector()
  static getItems(state: AutomotiveAftermarketFiltersStateModel): MonitoringFilterNameDictionary[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: AutomotiveAftermarketFiltersStateModel): boolean {
    return state.loaded;
  }

  @Action(AutomotiveAftermarketFiltersLoad)
  getAutomotiveAftermarketFilters(
    ctx: StateContext<AutomotiveAftermarketFiltersStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketFiltersService.getAutomotiveAftermarketFilters().pipe(
      map((filters: MonitoringFilterNameDictionary[]) => {
        ctx.patchState({ items: filters, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketFiltersUpdate)
  updateAutomotiveAftermarketFilters(
    ctx: StateContext<AutomotiveAftermarketFiltersStateModel>,
    { payload }: AutomotiveAftermarketFiltersUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketFiltersService.updateAutomotiveAftermarketFilters(payload).pipe(
      map((filters: MonitoringFilterNameDictionary[]) => {
        ctx.patchState({ items: filters, loaded: true });
        this.notificationService.success('api.transportFilters.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketFiltersInitialState)
  setInitialState(ctx: StateContext<AutomotiveAftermarketFiltersInitialState>): void {
    ctx.setState(initialState);
  }
}
